import * as React from "react";
import { IoCheckmark } from "react-icons/io5";

import { Loader } from "./Loader";

const STEP_MS = 550;

/**
 * S4 is the only node that sends a thinking_sequence — the tools it consulted to compose the
 * package. The response has already arrived; revealing the steps in sequence is purely
 * presentational, and onDone fires once the last one lands so the caller can show the package.
 */
export function ThinkingSequence({
  steps,
  onDone,
}: {
  steps: string[];
  onDone: () => void;
}) {
  const [shown, setShown] = React.useState(1);
  const doneRef = React.useRef(onDone);
  doneRef.current = onDone;

  React.useEffect(() => {
    if (shown >= steps.length) {
      const timer = setTimeout(() => doneRef.current(), STEP_MS);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => setShown(n => n + 1), STEP_MS);
    return () => clearTimeout(timer);
  }, [shown, steps.length]);

  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-2 pt-1">
      {steps.slice(0, shown).map((step, i) =>
        i < shown - 1 ? (
          <div
            key={i}
            className="flex animate-fade-in-up items-center gap-2 text-sm text-gray-500"
          >
            <IoCheckmark className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            <span>{step}</span>
          </div>
        ) : (
          <div key={i} className="flex animate-fade-in-up items-center gap-2">
            <Loader variant="circular" size="sm" />
            <Loader variant="text-shimmer" size="sm" text={step} />
          </div>
        )
      )}
    </div>
  );
}
